// ConversationManager.js - Version améliorée avec propositions enrichies et liens intégrés

import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import sourceService from '../CP-Sourcing/SourceService';
import { askGPT } from '../components/OpenAIService';
import EnhancedMarkdown from '../CP-Search/EnhancedMarkDown'; 

import { 
  Lightbulb, 
  Loader2,
  CheckCircle, 
  MessageCircle,
  ChevronRight,
  RefreshCw,
  Sparkles,
  Copy,
  RotateCcw,
  ExternalLink,
  Zap,
  AlertCircle,
  Target,
  Briefcase,
  Edit3,
  Send,
  ChevronDown,
  ChevronUp,
  Shield,
  WifiOff
} from 'lucide-react';

// ================================
// FONCTIONS UTILITAIRES AMÉLIORÉES
// ================================

// Fonction pour formater la réponse IA - SIMPLIFIÉE (pas besoin d'ajouter les liens à la fin)
const formatAIResponseWithLinks = (rawResponse, relevantLinks = []) => {
  if (!rawResponse) return '';
  
  // Les liens sont déjà intégrés dans la réponse, on fait juste un nettoyage léger
  return rawResponse
    .replace(/\n{3,}/g, '\n\n') // Normaliser les sauts de ligne multiples
    .trim();
};

// Fonction simplifiée pour formater le texte des propositions
const formatProposalText = (text) => {
  if (!text) return [];
  
  const cleanText = text
    .replace(/#{1,6}\s?/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  const paragraphs = cleanText.split(/\n\s*\n/).filter(p => p.trim());
  
  return paragraphs.map((paragraph, index) => ({
    content: paragraph.trim(),
    index: index
  }));
};

// ================================
// GÉNÉRATION DES RESSOURCES AVEC SOURCESERVICE (INCHANGÉE)
// ================================

/**
 * Génère des sources pertinentes et accessibles via sourceService
 * Utilise OpenAI comme provider et le mode WithFallback pour garantir l'accessibilité
 */
const generateRelevantSources = async (objective, askGPT, competenceId) => {
  console.log('🔍 Génération de sources via sourceService');
  
  try {
    // 1. Authentification Firebase
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('Utilisateur non authentifié');
    }
    
    const token = await auth.currentUser.getIdToken(true);
    
    // 2. Configuration optimisée pour rapidité + accessibilité
    const searchParams = {
      userId: auth.currentUser.uid,
      competenceId: competenceId,
      userObjective: objective,
      query: objective,
      
      // Paramètres de rapidité
      maxSources: 5,
      useAI: true,
      
      // Provider 
      selectedProviders: ['Google'],
      
      // Mode WithFallback = vérification + récupération intelligente
      verifyAccessibility: false,
      enableDomainFallback: false,
      detectParking: false,
      accessibilityMode: 'enhanced',
      
      // Options d'accessibilité pour rapidité
      accessibilityOptions: {
        enableDomainFallback: true,
        detectParking: true,
        maxConcurrent: 3
      },
      
      // Optimisations pour rapidité
      generateSummaries: false,
      preferredSourceTypes: null,
      
      // Paramètres linguistiques
      languages: ['fr'],
      country: '',
      
      // Options de localisation et traduction désactivées pour rapidité
      enableGeographicLocalization: false,
      translateToTargetLanguage: false
    };
    
    console.log('⚡ Appel sourceService avec configuration IA & WithFallback');
    const startTime = Date.now();
    
    // 3. Appel à sourceService
    const result = await sourceService.getRecommendedSources(searchParams, token);
    
    const endTime = Date.now();
    console.log(`✅ Sources reçues en ${endTime - startTime}ms`);
    
    // 4. Vérification du résultat
    if (!result || !result.sources || result.sources.length === 0) {
      console.warn('❌ Aucune source reçue de sourceService');
      throw new Error('Aucune source disponible');
    }
    
    console.log(`📊 ${result.sources.length} sources reçues de sourceService`);
    
    // 5. Sélection et transformation des 3 meilleures sources
    const selectedSources = result.sources
      .slice(0, 3)
      .map(source => ({
        // Propriétés compatibles avec l'interface actuelle
        title: source.title || 'Source recommandée',
        description: source.description || source.summary || 'Source pertinente pour votre objectif',
        actionnable: source.actionnable || `Consulter ${source.title || 'cette ressource'}`,
        type: source.sourceType || source.category || 'OpenAI',
        
        // URLs avec gestion du fallback
        url: source.recommendedUrl || source.usableUrl || source.url || source.link,
        originalUrl: source.originalUrl || source.url || source.link,
        finalUrl: source.recommendedUrl || source.usableUrl || source.url || source.link,
        
        // Informations d'accessibilité
        accessible: true,
        accessibilityStatus: source.accessibilityStatus || 'verified',
        hasUrlFallback: source.fallbackApplied || source.hasUrlFallback || false,
        fallbackMethod: source.fallbackMethod || null,
        
        // Métadonnées supplémentaires
        responseTime: source.responseTime || null,
        error: null,
        backendType: 'sourceService',
        message: source.fallbackApplied ? 
          'URL récupérée via fallback intelligent' : 
          'URL directement accessible',
        
        // Détails provider
        provider: 'Aladinn',
        domain: source.domain || null,
        verificationDetails: source.verificationDetails || null
      }));
    
    console.log(`✅ ${selectedSources.length} sources transformées et prêtes`);
    return selectedSources;
    
  } catch (error) {
    console.warn('❌ Échec sourceService, utilisation du fallback:', error.message);
    
    // 6. Fallback vers l'ancienne méthode si sourceService échoue
    return await generateAIResourcesFallback(objective, askGPT);
  }
};

/**
 * Fonction de fallback - ancienne méthode simplifiée
 */
const generateAIResourcesFallback = async (objective, askGPT) => {
  console.log('🔄 Utilisation de la méthode de fallback');
  
  const prompt = `
Tu es un expert en curation de ressources fiables.
OBJECTIF : « ${objective} »

CONTRAINTE ABSOLUE
------------------
1. Retourne exactement 3 ressources.
2. Chaque ressource contient UNIQUEMENT :
   - "title"  : titre court
   - "description" : 1 phrase
   - "actionnable" : 1 verbe à l'infinitif + complément
   - "type"   : Education | Government | Non Profit | Commercial | Media 
   - "url"    : URL complète et spécifique
3. Pas d'explications, pas de markdown — uniquement un JSON STRICT 

{
  "resources": [
    { "title": "...", "description": "...", "actionnable": "...", "type": "...", "url": "https://exemple.com/page-specifique" }
  ]
}
`;

  try {
    const raw = await askGPT(prompt);
    if (!raw) throw new Error('Réponse vide de GPT');

    const clean = raw.replace(/```json?|```/g, '').trim();
    const { resources = [] } = JSON.parse(clean);

    return resources.slice(0, 3).map(r => ({
      ...r,
      accessible: false,
      finalUrl: r.url,
      hasUrlFallback: false,
      fallbackMethod: 'client_fallback',
      backendType: 'fallback',
      message: 'Source non vérifiée (mode fallback)'
    }));
    
  } catch (error) {
    console.error('Erreur fallback:', error);
    return [];
  }
};

// ================================
// FONCTION DE GÉNÉRATION DES QUESTIONS GUIDÉES (INCHANGÉE)
// ================================

const generateGuidedQuestions = async (objective, askGPT) => {
  const prompt = `
Analyse cet objectif utilisateur et génère exactement 3 questions pertinentes pour le préciser :

Objectif : "${objective}"

Tu dois générer 3 questions qui aideront l'utilisateur à clarifier son intention, son contexte et ses besoins spécifiques.

Pour chaque question, propose 4-6 choix de réponses concrètes et actionnables.

Réponds UNIQUEMENT avec un JSON valide dans ce format exact :
{
  "questions": [
    {
      "id": "question1",
      "question": "Question claire et directe ?",
      "category": "intention|context|specificity",
      "choices": [
        {"id": "choice1", "label": "Choix court", "description": "Description explicative"},
        {"id": "choice2", "label": "Choix court", "description": "Description explicative"}
      ]
    }
  ]
}

IMPORTANT : 
- Questions courtes et claires
- Choix variés et pertinents pour l'objectif donné
- Labels de choix : maximum 4-5 mots
- Descriptions : une phrase courte
- Exactement 3 questions
- JSON valide uniquement, pas de texte avant/après
`;

  try {
    const response = await askGPT(prompt);
    
    if (response) {
      const cleanResponse = response
        .replace(/```json|```/g, '')
        .replace(/^[^{]*/, '')
        .replace(/[^}]*$/, '')
        .trim();
      
      const parsed = JSON.parse(cleanResponse);
      
      if (parsed.questions && Array.isArray(parsed.questions) && parsed.questions.length === 3) {
        return parsed.questions;
      }
    }
    
    throw new Error('Format de réponse invalide');
  } catch (error) {
    console.error('Erreur génération questions:', error);
    
    // Questions de fallback
    return [
      {
        id: "intention",
        question: "Quelle est votre intention principale ?",
        category: "intention",
        choices: [
          { id: 'learn', label: 'Apprendre', description: 'Acquérir des connaissances' },
          { id: 'find', label: 'Trouver', description: 'Rechercher des informations' },
          { id: 'compare', label: 'Comparer', description: 'Évaluer des options' },
          { id: 'solve', label: 'Résoudre', description: 'Trouver une solution' },
          { id: 'create', label: 'Créer', description: 'Développer quelque chose' }
        ]
      },
      {
        id: "context",
        question: "Dans quel contexte travaillez-vous ?",
        category: "context",
        choices: [
          { id: 'personal', label: 'Personnel', description: 'Développement personnel' },
          { id: 'professional', label: 'Professionnel', description: 'Cadre de travail' },
          { id: 'academic', label: 'Académique', description: 'Études ou recherche' },
          { id: 'entrepreneurial', label: 'Entrepreneurial', description: 'Projet d\'entreprise' }
        ]
      },
      {
        id: "priority",
        question: "Quel aspect vous préoccupe le plus ?",
        category: "specificity",
        choices: [
          { id: 'speed', label: 'Rapidité', description: 'Obtenir des résultats vite' },
          { id: 'quality', label: 'Qualité', description: 'Résultats de haute qualité' },
          { id: 'cost', label: 'Coût', description: 'Solution économique' },
          { id: 'simplicity', label: 'Simplicité', description: 'Approche simple' }
        ]
      }
    ];
  }
};

// ================================
// COMPOSANT D'INTRODUCTION AUX QUESTIONS GUIDÉES (SIMPLIFIÉ)
// ================================

const GuidedQuestionsIntro = ({ guidedAnswersCount }) => {
    return null;
  };


// ================================
// COMPOSANT QUESTIONS GUIDÉES (INCHANGÉ)
// ================================

const AIGeneratedQuestion = ({ 
  question, 
  selectedChoice, 
  customText, 
  onChoiceSelect, 
  onCustomTextSubmit,
  isCompleted,
  questionIndex,
  totalQuestions
}) => {
  const [isExpanded, setIsExpanded] = useState(!isCompleted);
  const [showCustomField, setShowCustomField] = useState(false);
  const [localCustomText, setLocalCustomText] = useState(customText || '');

  const handleChoiceClick = (choice) => {
    onChoiceSelect(choice);
    setShowCustomField(false);
    setIsExpanded(false);
  };

  const handleCustomSubmit = () => {
    if (localCustomText.trim()) {
      onCustomTextSubmit(localCustomText.trim());
      setShowCustomField(false);
      setIsExpanded(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'intention': return <Target className="h-4 w-4" />;
      case 'context': return <Briefcase className="h-4 w-4" />;
      case 'specificity': return <Lightbulb className="h-4 w-4" />;
      default: return <MessageCircle className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'intention': return 'text-blue-600 border-blue-200 bg-blue-50';
      case 'context': return 'text-green-600 border-green-200 bg-green-50';
      case 'specificity': return 'text-purple-600 border-purple-200 bg-purple-50';
      default: return 'text-gray-600 border-gray-200 bg-gray-50';
    }
  };

  return (
    <div className={`border rounded-lg transition-all shadow-sm ${
      isCompleted ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50'
    }`}>
      {/* En-tête cliquable */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`p-2 rounded-lg mr-3 ${getCategoryColor(question.category)}`}>
              {getCategoryIcon(question.category)}
            </div>
            <div>
              <h3 className="font-medium text-gray-800">{question.question}</h3>
              <p className="text-xs text-gray-500">Question {questionIndex + 1} sur {totalQuestions}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {isCompleted && <CheckCircle className="h-5 w-5 text-green-500" />}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
        
        {/* Affichage de la réponse si complétée et rideau fermé */}
        {isCompleted && !isExpanded && (
          <div className="mt-2 p-2 bg-white rounded border text-sm">
            <strong>Réponse :</strong> {selectedChoice?.label || customText}
          </div>
        )}
      </div>

      {/* Contenu expansible */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Choix générés par l'IA */}
          <div className="grid gap-2 mb-3 mt-3">
            {question.choices.map((choice) => (
              <button
                key={choice.id}
                onClick={() => handleChoiceClick(choice)}
                className={`text-left p-3 rounded-lg border transition-all hover:border-blue-300 hover:bg-blue-50 ${
                  selectedChoice?.id === choice.id 
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                    : 'border-gray-200'
                }`}
              >
                <div className="font-medium text-sm text-gray-800">{choice.label}</div>
                <div className="text-xs text-gray-600 mt-1">{choice.description}</div>
              </button>
            ))}
          </div>

          {/* Option personnalisée */}
          {!showCustomField ? (
            <button
              onClick={() => setShowCustomField(true)}
              className="w-full p-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-all"
            >
              <Edit3 className="h-4 w-4 inline mr-2" />
              Répondre autrement...
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={localCustomText}
                onChange={(e) => setLocalCustomText(e.target.value)}
                placeholder="Votre réponse personnalisée..."
                className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCustomSubmit()}
                autoFocus
              />
              <button
                onClick={handleCustomSubmit}
                disabled={!localCustomText.trim()}
                className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ================================
// COMPOSANT AI RESPONSE AMÉLIORÉ AVEC MARKDOWN
// ================================

const AIResponseTab = ({ 
    aiResponse, 
    isLoadingResponse, 
    onGenerateResponse, 
    onRegenerateResponse,
    userObjective,
    objectiveHasChanged = false,
    resourceLinks = []
  }) => {
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    const handleCopyResponse = () => {
      if (aiResponse) {
        const plainText = formatAIResponseWithLinks(aiResponse, resourceLinks);
        navigator.clipboard.writeText(plainText);
        setShowCopySuccess(true);
        setTimeout(() => setShowCopySuccess(false), 2000);
      }
    };

    if (isLoadingResponse) {
      return (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Génération de votre réponse personnalisée</p>
            <p className="text-sm text-gray-500 mt-1">Recherche de sources via Aladinn...</p>
          </div>
        </div>
      );
    }

    if (!aiResponse) {
      return (
        <div className="bg-white p-6 rounded-lg border border-gray-200 text-center">
          <Sparkles className="h-12 w-12 mx-auto text-purple-400 mb-3" />
          <h4 className="text-lg font-medium text-gray-800 mb-2">Réponse immédiate disponible</h4>
          <p className="text-gray-600 text-sm max-w-md mx-auto mb-4">
            Aladinn peut analyser votre objectif et vous fournir des conseils pratiques avec sources vérifiées.
          </p>
          <button
            onClick={onGenerateResponse}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium flex items-center mx-auto hover:from-purple-600 hover:to-pink-600 transition-all"
          >
            <Zap className="h-5 w-5 mr-2" />
            Obtenir une réponse IA
          </button>
        </div>
      );
    }

    // Formater la réponse avec les liens intégrés pour EnhancedMarkdown
    const formattedResponseWithLinks = formatAIResponseWithLinks(aiResponse, resourceLinks);

    return (
      <div className="space-y-6">
        {/* Notification de mise à jour nécessaire */}
        {objectiveHasChanged && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-orange-800 font-medium mb-2">🔄 Objectif modifié</h4>
                <p className="text-sm text-orange-700 mb-3">
                  Votre objectif a été mis à jour. Regénérez la réponse pour des conseils adaptés.
                </p>
                <button
                  onClick={onRegenerateResponse}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg font-medium flex items-center text-sm"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Générer de nouvelles propositions
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Réponse IA avec EnhancedMarkdown */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          {/* Header similaire à SynthesisCard */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 border-b border-purple-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="h-6 w-6 text-purple-600" />
                <h3 className="text-xl font-semibold text-purple-800">
                  Propositions personnalisées
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCopyResponse}
                  className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                  title="Copier le texte"
                >
                  {showCopySuccess ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={onRegenerateResponse}
                  className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                  title="Regénérer"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Contenu principal avec EnhancedMarkdown */}
          <div className="p-6">
            <div className="text-gray-600">
              <EnhancedMarkdown content={formattedResponseWithLinks} />
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 italic">
                Propositions générées par Aladinn avec sources vérifiées automatiquement
              </p>
              <div className="flex items-center space-x-4 text-xs text-gray-400">
                {resourceLinks.length > 0 && (
                  <span className="flex items-center">
                    <Shield className="h-3 w-3 mr-1" />
                    {resourceLinks.filter(link => link.accessible).length}/{resourceLinks.length} sources accessibles
                  </span>
                )}
                <span>{new Date().toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          </div>
        </div>

        // Dans le composant AIResponseTab, remplacer la section des sources (vers la ligne 1100) :

        {/* Section des sources avec cartes clickables */}
        {resourceLinks.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 border-b border-green-100">
              <h4 className="text-lg font-semibold text-green-800 flex items-center">
                <ExternalLink className="h-5 w-5 mr-2 text-green-600" />
                Détails des sources vérifiées
              </h4>
              <p className="text-sm text-green-600 mt-1">
                Cliquez sur une carte pour accéder à la source
              </p>
            </div>
            <div className="p-6">
              <div className="grid gap-4">
                {resourceLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.finalUrl || link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-start p-4 border rounded-lg transition-all cursor-pointer hover:shadow-md hover:scale-[1.02] ${
                      link.accessible 
                        ? 'border-green-200 bg-green-50 hover:border-green-300 hover:bg-green-100' 
                        : 'border-red-200 bg-red-50 hover:border-red-300 hover:bg-red-100'
                    }`}
                  >
                    <div className={`flex-shrink-0 p-2 rounded-lg mr-4 ${
                      link.accessible 
                        ? 'bg-green-100' 
                        : 'bg-red-100'
                    }`}>
                      {link.accessible ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <WifiOff className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h5 className={`font-semibold mb-1 ${
                        link.accessible ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {link.title}
                      </h5>
                      <p className="text-sm text-gray-600 mb-2">
                        {link.description}
                      </p>
                      
                      <div className="space-y-1">
                        <p className={`text-xs font-mono px-2 py-1 rounded ${
                          link.accessible 
                            ? 'text-blue-600 bg-blue-50' 
                            : 'text-red-600 bg-red-100'
                        }`}>
                          {link.finalUrl || link.url}
                        </p>
                        
                        {link.hasUrlFallback && (
                          <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            URL récupérée via {link.fallbackMethod}
                          </p>
                        )}
                        
                        <p className="text-xs text-gray-500">
                          Provider: {link.provider} • Type: {link.type}
                        </p>
                      </div>
                    </div>
                    
                    {/* Icône de lien externe */}
                    <div className="flex-shrink-0 ml-2">
                      <ExternalLink className={`h-4 w-4 ${
                        link.accessible ? 'text-green-600' : 'text-red-600'
                      }`} />
                    </div>
                  </a>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 flex items-center">
                  <Shield className="h-3 w-3 mr-1" />
                  Sources sélectionnées par Aladinn et vérifiées via sourceService. 
                  Les URLs sont testées automatiquement avec récupération intelligente.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

// ================================
// COMPOSANT PRINCIPAL (SUITE - INCHANGÉ JUSQU'À generateAIResponse)
// ================================

const ConversationManager = ({
    userObjective,
    competenceId,
    conversationData,
    analysisData,
    onConversationUpdate,
    onAnalysisUpdate,
    onSaveData,
    onSwitchTab,
    onLaunchAnalysis,
    onObjectiveUpdate,
    mode = 'conversation'
  }) => {
    // ================================
    // ÉTATS LOCAUX
    // ================================
    const [aiQuestions, setAiQuestions] = useState(conversationData.aiQuestions || []);
    const [answers, setAnswers] = useState(conversationData.guidedAnswers || {});
    const [conversationPhase, setConversationPhase] = useState(conversationData.conversationPhase || 'generating');
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
    const [isLoadingAIResponse, setIsLoadingAIResponse] = useState(false);
    const [isUpdatingObjective, setIsUpdatingObjective] = useState(false);
    const [questionGenerationError, setQuestionGenerationError] = useState(null);
    const [relevantLinks, setRelevantLinks] = useState(conversationData.relevantLinks || []);

    // ================================
    // VARIABLES CALCULÉES
    // ================================
    const allQuestionsAnswered = aiQuestions.length > 0 && aiQuestions.every(q => answers[q.id]);
    const canUpdateObjective = allQuestionsAnswered;
    const guidedAnswersCount = Object.keys(answers).length;

    // ================================
    // GESTIONNAIRES D'ÉVÉNEMENTS (INCHANGÉS JUSQU'À generateAIResponse)
    // ================================

    // Gestion de la génération des questions
    const handleGenerateQuestions = () => {
      if (aiQuestions.length === 0) {
        setConversationPhase('generating');
        generateQuestionsFromObjective();
      }
    };

    // Générer les questions au démarrage
    useEffect(() => {
      if (userObjective && conversationPhase === 'generating' && aiQuestions.length === 0 && !isLoadingQuestions) {
        generateQuestionsFromObjective();
      }
    }, [userObjective, conversationPhase, aiQuestions.length, isLoadingQuestions]);

    // Fonction pour générer les questions
    const generateQuestionsFromObjective = async () => {
      setIsLoadingQuestions(true);
      setQuestionGenerationError(null);

      try {
        const questions = await generateGuidedQuestions(userObjective, askGPT);
        setAiQuestions(questions);
        setConversationPhase('guided');
        
        onConversationUpdate({
          aiQuestions: questions,
          conversationPhase: 'guided'
        });
      } catch (error) {
        console.error('Erreur génération questions:', error);
        setQuestionGenerationError('Erreur lors de la génération des questions');
      } finally {
        setIsLoadingQuestions(false);
      }
    };

    // Enrichir l'objectif basé sur les réponses
    const enrichObjective = async () => {
      if (!allQuestionsAnswered) return userObjective;

      const answersText = aiQuestions.map(q => {
        const answer = answers[q.id];
        return `${q.question} → ${answer.data.label}`;
      }).join('\n');

      const chatContext = '';

      const enrichmentPrompt = `
Objectif initial : "${userObjective}"

Réponses aux questions de précision :
${answersText}

Reformule cet objectif en intégrant naturellement les précisions apportées. 
L'objectif enrichi doit :
- Rester concis (maximum 2 phrases)
- Intégrer les éléments de contexte pertinents
- Être plus actionnable que l'objectif initial

Réponds uniquement avec l'objectif reformulé, sans explication.
`;

      try {
        const enrichedObjective = await askGPT(enrichmentPrompt);
        return enrichedObjective || userObjective;
      } catch (error) {
        console.error('Erreur enrichissement objectif:', error);
        return userObjective;
      }
    };

    // Gérer les réponses aux questions
    const handleChoiceSelect = (questionId, choice) => {
      const newAnswers = {
        ...answers,
        [questionId]: { type: 'choice', data: choice }
      };
      setAnswers(newAnswers);
      
      onConversationUpdate({
        guidedAnswers: newAnswers
      });
    };

    const handleCustomTextSubmit = (questionId, text) => {
      const newAnswers = {
        ...answers,
        [questionId]: { type: 'custom', data: { label: text, description: 'Réponse personnalisée' } }
      };
      setAnswers(newAnswers);
      
      onConversationUpdate({
        guidedAnswers: newAnswers
      });
    };

    // Mettre à jour l'objectif avec indicateur de chargement
    const updateObjectiveFromAnswers = async () => {
      if (!allQuestionsAnswered) return;
      
      setIsUpdatingObjective(true);
      
      try {
        const enrichedObjective = await enrichObjective();
        
        if (onObjectiveUpdate) {
          await onObjectiveUpdate(enrichedObjective);
        }
        
        onConversationUpdate({
          objectiveUpdatedFromAnswers: true,
          lastObjectiveUpdate: Date.now(),
          objectiveHasChangedSinceAI: true
        });
      } catch (error) {
        console.error('Erreur mise à jour objectif:', error);
      } finally {
        setIsUpdatingObjective(false);
      }
    };

    // ================================
    // FONCTION DE GÉNÉRATION DE RÉPONSE IA AMÉLIORÉE
    // ================================
    const generateAIResponse = async () => {
      if (!userObjective || isLoadingAIResponse) return;

      setIsLoadingAIResponse(true);

      try {
        let conversationContext = '';
        if (allQuestionsAnswered) {
          conversationContext = aiQuestions.map(q => {
            const answer = answers[q.id];
            return `${q.question} : ${answer.data.label}`;
          }).join('\n');
        }



        // D'abord générer les sources pour les intégrer dans le prompt
        console.log('🔍 Génération des sources via sourceService...');
        const aiResources = await generateRelevantSources(userObjective, askGPT, competenceId);
        console.log(`✅ ${aiResources.length} sources reçues et validées`);
        
        // Préparer les sources pour intégration dans le prompt
        const sourcesForPrompt = aiResources.filter(source => source.accessible).map((source, index) => 
          `Source ${index + 1}: "${source.title}" - ${source.description} [${source.finalUrl || source.url}]`
        ).join('\n');

        // NOUVEAU PROMPT OPTIMISÉ AVEC SOURCES INTÉGRÉES
        const prompt = `
Tu es Aladinn, un assistant expert qui aide les utilisateurs à atteindre leurs objectifs.

Objectif de l'utilisateur : "${userObjective}"

${conversationContext ? `Contexte détaillé :
${conversationContext}` : ''}

Sources vérifiées disponibles pour intégration :
${sourcesForPrompt}

IMPORTANT - Tu dois répondre en markdown avec cette structure EXACTE :

**1. [Titre de la première proposition]**

[Description détaillée de la première action concrète à entreprendre. Intègre UNE source pertinente sous forme de lien : [Nom de la source](URL)]

**2. [Titre de la deuxième proposition]** 

[Description détaillée de la deuxième action complémentaire. Intègre UNE source pertinente sous forme de lien : [Nom de la source](URL)]

**3. [Titre de la troisième proposition]**

[Description détaillée de la troisième action pour maximiser les chances de succès. Intègre UNE source pertinente sous forme de lien : [Nom de la source](URL)]

## 🚀 Prochaines étapes

[Une phrase de conclusion dirigeant vers l'action]

RÈGLES STRICTES :
- Utilise exactement cette structure markdown avec sauts de ligne entre chaque section
- Intègre OBLIGATOIREMENT un lien cliquable dans chaque proposition (format : [Nom](URL))
- Propositions courtes et actionnables (max 2-3 phrases chacune)
- Sépare chaque proposition par une ligne vide
- Sépare chaque point d'attention par une ligne vide
- Points d'attention précis et utiles
- Maximum 300 mots au total
- Ton professionnel mais encourageant
- N'ajoute PAS de section "Sources" à la fin
`;

        const response = await askGPT(prompt);

        if (response) {
          setRelevantLinks(aiResources);
          
          onConversationUpdate({
            aiResponse: response,
            aiResponseGenerated: true,
            originalObjectiveForAI: userObjective,
            objectiveHasChangedSinceAI: false,
            relevantLinks: aiResources
          });
        }
      } catch (error) {
        console.error('Erreur lors de la génération de la réponse IA:', error);
      } finally {
        setIsLoadingAIResponse(false);
      }
    };

    // Régénérer la réponse IA
    const regenerateAIResponse = async () => {
      onConversationUpdate({
        aiResponse: '',
        aiResponseGenerated: false,
        originalObjectiveForAI: '',
        objectiveHasChangedSinceAI: false,
        relevantLinks: []
      });
      setRelevantLinks([]);
      await generateAIResponse();
    };

    // Recommencer avec de nouvelles questions
    const handleRegenerateQuestions = () => {
      setAnswers({});
      setAiQuestions([]);
      setConversationPhase('generating');
      setQuestionGenerationError(null);
      
      onConversationUpdate({
        aiQuestions: [],
        guidedAnswers: {},
        conversationPhase: 'generating'
      });
    };

    // ================================
    // RENDU CONDITIONNEL PAR MODE
    // ================================

    // Mode AI Response
    if (mode === 'ai_response') {
      return (
        <AIResponseTab
          aiResponse={conversationData.aiResponse}
          isLoadingResponse={isLoadingAIResponse}
          onGenerateResponse={generateAIResponse}
          onRegenerateResponse={regenerateAIResponse}
          userObjective={userObjective}
          objectiveHasChanged={conversationData.objectiveHasChangedSinceAI}
          resourceLinks={relevantLinks}
        />
      );
    }

    // ================================
    // RENDU PRINCIPAL - MODE CONVERSATION (SUITE INCHANGÉE)
    // ================================
    return (
      <div className="space-y-6">
        
        {/* Introduction aux questions guidées */}
        <GuidedQuestionsIntro guidedAnswersCount={guidedAnswersCount} />



        {/* ===== PHASE GÉNÉRATION DES QUESTIONS ===== */}
        {conversationPhase === 'generating' && (
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Lightbulb className="h-8 w-8 text-blue-500 mr-3" />
                {isLoadingQuestions ? (
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-red-500" />
                )}
              </div>
              
              {isLoadingQuestions ? (
                <div>
                  <h3 className="font-medium text-blue-800 mb-2">Analyse en cours...</h3>
                  <p className="text-sm text-blue-700">
                    Aladinn analyse votre objectif pour générer des questions pertinentes.
                  </p>
                </div>
              ) : questionGenerationError ? (
                <div>
                  <h3 className="font-medium text-red-800 mb-2">Erreur de génération</h3>
                  <p className="text-sm text-red-700 mb-4">{questionGenerationError}</p>
                  <button
                    onClick={generateQuestionsFromObjective}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4 inline mr-2" />
                    Réessayer
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ===== PHASE QUESTIONS GUIDÉES ===== */}
        {conversationPhase === 'guided' && (
          <div className="space-y-4">
            {aiQuestions.map((question, index) => (
              <AIGeneratedQuestion
                key={question.id}
                question={question}
                questionIndex={index}
                totalQuestions={aiQuestions.length}
                selectedChoice={answers[question.id]?.type === 'choice' ? answers[question.id].data : null}
                customText={answers[question.id]?.type === 'custom' ? answers[question.id].data.label : ''}
                onChoiceSelect={(choice) => handleChoiceSelect(question.id, choice)}
                onCustomTextSubmit={(text) => handleCustomTextSubmit(question.id, text)}
                isCompleted={!!answers[question.id]}
              />
            ))}

            {/* Actions après avoir répondu aux questions */}
            {allQuestionsAnswered && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-green-800 mb-1 flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Questions complétées
                    </h3>
                    <p className="text-sm text-green-700">
                      {canUpdateObjective 
                        ? (conversationData.objectiveUpdatedFromAnswers 
                            ? "Vous pouvez mettre à jour à nouveau votre objectif avec de nouvelles réponses"
                            : "Mettez à jour votre objectif avec vos réponses")
                        : "Objectif mis à jour ! Vous pouvez maintenant consulter les autres onglets."
                      }
                    </p>
                  </div>
                  <div className="flex gap-3">
                    {canUpdateObjective && (
                      <button
                        onClick={updateObjectiveFromAnswers}
                        disabled={isUpdatingObjective}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                      >
                        {isUpdatingObjective ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Mise à jour...
                          </>
                        ) : (
                          <>
                            <Target className="h-4 w-4 mr-2" />
                            Mettre à jour l'objectif
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={handleRegenerateQuestions}
                      className="px-3 py-2 text-green-600 hover:text-green-800 text-sm flex items-center border border-green-300 rounded-lg hover:bg-green-100 transition-colors"
                      title="Générer de nouvelles questions"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Nouvelles questions
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== SECTIONS D'INFORMATIONS ===== */}
        
        {/* Actions recommandées selon l'état */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-100">
          <div className="flex items-start space-x-3">
            <Target className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-indigo-800">Prochaines étapes recommandées</p>
              <div className="mt-2 space-y-1">
                {!allQuestionsAnswered && (
                  <p className="text-xs text-indigo-700">
                    • Répondez aux 3 questions pour structurer votre recherche
                  </p>
                )}
                
                {allQuestionsAnswered && !conversationData.objectiveUpdatedFromAnswers && (
                  <p className="text-xs text-indigo-700">
                    • Mettez à jour votre objectif avec vos nouvelles précisions
                  </p>
                )}
                {conversationData.objectiveUpdatedFromAnswers && !conversationData.aiResponseGenerated && (
                  <p className="text-xs text-indigo-700">
                    • Obtenez des propositions IA personnalisées dans l'onglet "Propositions"
                  </p>
                )}
                {conversationData.aiResponseGenerated && !analysisData.completed && (
                  <p className="text-xs text-indigo-700">
                    • Lancez l'analyse sémantique pour optimiser votre recherche
                  </p>
                )}
                {analysisData.completed && !analysisData.readyToSearch && (
                  <p className="text-xs text-indigo-700">
                    • Validez votre analyse pour préparer la recherche 360°
                  </p>
                )}
                {analysisData.readyToSearch && (
                  <p className="text-xs text-indigo-700">
                    • Votre recherche est prête ! Lancez la recherche 360° pour obtenir des sources spécialisées
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notification d'analyse terminée */}
        {analysisData.completed && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <div>
                  <p className="text-green-700 font-medium">Analyse sémantique terminée</p>
                  <p className="text-green-600 text-sm">
                    Intention : {analysisData.results?.intention || 'Non définie'}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                {!conversationData.aiResponseGenerated && (
                  <button
                    onClick={() => onSwitchTab('ai_response')}
                    className="text-purple-600 hover:text-purple-800 flex items-center text-sm font-medium"
                  >
                    <span>Propositions</span>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                )}
                <button
                  onClick={() => onSwitchTab('analysis')}
                  className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium"
                >
                  <span>Voir l'analyse</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== FOOTER INFORMATIF ===== */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-4">
              <span>❓ Questions guidées intelligentes</span>
              <span>🎯 Enrichissement automatique d'objectif</span>
              <span>🤖 Sources web vérifiées</span>
            </div>
            <div className="flex items-center space-x-2">
              <span>Optimisé par Aladinn + sourceService</span>
              <Sparkles className="h-3 w-3" />
            </div>
          </div>
        </div>
        
      </div>
    );
  };

export default ConversationManager;
